import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  type DateSpecificEntry,
  type DateSpecificParams,
  type RuleFormProps,
  majorStrToMinorOpt,
  minorToMajorStr,
  pctStrToNumOpt,
  intStrOpt,
} from '@/lib/pricingRules';

// REFERENCE FORM for the entry-list shape. Other entry-list forms (seasonal,
// booking_restriction) mirror this structure:
//   - local string "draft" state seeded once from params (avoids the
//     controlled-number-input pitfall where typing "-" or "10." resets)
//   - rebuild the typed params from drafts on EVERY change and emit upward
//   - add / remove rows; the parent (RulesEditor) owns persistence
//
// date_specific entry: a date range with EITHER a %/abs adjustment OR a hard
// `set_rate_minor` (mutually exclusive — enforced by the mode toggle), plus an
// optional min-stay. Money is entered in major units, stored as minor.

interface EntryDraft {
  from: string;
  to: string;
  mode: 'adjust' | 'fixed';
  pct: string;
  abs: string;
  setRate: string;
  minStay: string;
}

function entryToDraft(e: DateSpecificEntry): EntryDraft {
  const fixed = e.set_rate_minor != null;
  return {
    from: e.from ?? '',
    to: e.to ?? '',
    mode: fixed ? 'fixed' : 'adjust',
    pct: e.pct != null ? String(e.pct) : '',
    abs: minorToMajorStr(e.abs_minor),
    setRate: minorToMajorStr(e.set_rate_minor),
    minStay: e.min_stay != null ? String(e.min_stay) : '',
  };
}

function draftToEntry(d: EntryDraft): DateSpecificEntry {
  const e: DateSpecificEntry = { from: d.from.trim(), to: d.to.trim() };
  if (d.mode === 'fixed') {
    const sr = majorStrToMinorOpt(d.setRate);
    if (typeof sr === 'number') e.set_rate_minor = sr;
  } else {
    const p = pctStrToNumOpt(d.pct);
    if (typeof p === 'number') e.pct = p;
    const a = majorStrToMinorOpt(d.abs);
    if (typeof a === 'number') e.abs_minor = a;
  }
  const ms = intStrOpt(d.minStay);
  if (typeof ms === 'number') e.min_stay = ms;
  return e;
}

export function DateSpecificRuleForm({ params, onChange }: RuleFormProps<DateSpecificParams>) {
  const [drafts, setDrafts] = useState<EntryDraft[]>(() =>
    (params.entries ?? []).map(entryToDraft),
  );

  function commit(next: EntryDraft[]) {
    setDrafts(next);
    onChange({ entries: next.map(draftToEntry) });
  }

  function patch(i: number, p: Partial<EntryDraft>) {
    commit(drafts.map((d, idx) => (idx === i ? { ...d, ...p } : d)));
  }

  return (
    <div className="flex flex-col gap-3">
      {drafts.map((d, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="From"
              type="date"
              value={d.from}
              onChange={(e) => patch(i, { from: e.target.value })}
            />
            <Input
              label="To"
              type="date"
              value={d.to}
              onChange={(e) => patch(i, { to: e.target.value })}
            />
          </div>

          <div className="mt-3 max-w-[12rem]">
            <Select
              label="Pricing"
              options={[
                { value: 'adjust', label: 'Adjust (%/abs)' },
                { value: 'fixed', label: 'Set fixed rate' },
              ]}
              value={d.mode}
              onChange={(e) => patch(i, { mode: e.target.value as EntryDraft['mode'] })}
            />
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {d.mode === 'adjust' ? (
              <>
                <Input
                  label="Adjustment (%)"
                  type="number"
                  step="0.1"
                  value={d.pct}
                  onChange={(e) => patch(i, { pct: e.target.value })}
                  placeholder="e.g. -10"
                />
                <Input
                  label="Absolute (€)"
                  type="number"
                  step="0.01"
                  value={d.abs}
                  onChange={(e) => patch(i, { abs: e.target.value })}
                  placeholder="optional"
                />
              </>
            ) : (
              <Input
                label="Fixed rate (€)"
                type="number"
                step="0.01"
                min={0}
                value={d.setRate}
                onChange={(e) => patch(i, { setRate: e.target.value })}
              />
            )}
            <Input
              label="Min stay (nights)"
              type="number"
              min={1}
              value={d.minStay}
              onChange={(e) => patch(i, { minStay: e.target.value })}
              placeholder="optional"
            />
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => commit(drafts.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="h-4 w-4" />
              Remove range
            </Button>
          </div>
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            commit([...drafts, { from: '', to: '', mode: 'adjust', pct: '0', abs: '', setRate: '', minStay: '' }])
          }
        >
          <Plus className="h-4 w-4" />
          Add date range
        </Button>
      </div>
    </div>
  );
}
